/* ==========================================================================
   HPHS — main.js
   Vanilla JS. No build step, no backend. Everything below simulates behaviour
   locally so the prototype can be clicked through in a stakeholder session.

   INTEGRATION POINTS (not implemented here, by design):
     [BOOKSY]  scheduling handoff  — see initBooking()
     [SHOPIFY] cart + checkout     — see Cart
     [HUBSPOT] form submission     — see initForms()
     [AIRCALL] click-to-call       — tel: links in the markup
   ========================================================================== */

(function () {
  'use strict';

  const D = window.HPHS || {};
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));
  const money = (n) => '$' + Number(n).toFixed(2).replace(/\.00$/, '');

  /* ------------------------------------------------------------------
     Storage — localStorage where available, memory otherwise.
     ------------------------------------------------------------------ */
  const Store = (function () {
    let mem = {};
    let ok = false;
    try {
      window.localStorage.setItem('__t', '1');
      window.localStorage.removeItem('__t');
      ok = true;
    } catch (e) { ok = false; }
    return {
      get(k) {
        try { return ok ? window.localStorage.getItem(k) : (mem[k] || null); }
        catch (e) { return mem[k] || null; }
      },
      set(k, v) {
        try { ok ? window.localStorage.setItem(k, v) : (mem[k] = v); }
        catch (e) { mem[k] = v; }
      }
    };
  })();

  /* ------------------------------------------------------------------
     Toast
     ------------------------------------------------------------------ */
  let toastEl, toastTimer;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 3200);
  }

  /* ------------------------------------------------------------------
     Header: mobile drawer, dropdown, sticky book bar
     ------------------------------------------------------------------ */
  function initHeader() {
    const burgers = $$('.burger');
    const drawer = $('#nav-drawer');
    const header = $('.site-header');

    // Transparent over the hero, solid once the page moves. 450ms fade.
    if (header && !document.body.classList.contains('header-solid')) {
      const sync = () => header.classList.toggle('is-solid', window.scrollY > 24);
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => { sync(); ticking = false; });
      }, { passive: true });
      sync();
    }

    if (burgers.length && drawer) {
      const close = () => {
        drawer.classList.remove('is-open');
        burgers.forEach(b => b.setAttribute('aria-expanded', 'false'));
        document.body.classList.remove('is-locked');
      };
      const open = () => {
        drawer.classList.add('is-open');
        if (header) header.classList.add('is-solid');
        burgers.forEach(b => b.setAttribute('aria-expanded', 'true'));
        document.body.classList.add('is-locked');
        const first = $('a, button', drawer);
        if (first) first.focus();
      };
      burgers.forEach(b => {
        b.addEventListener('click', (e) => {
          e.preventDefault();
          drawer.classList.contains('is-open') ? close() : open();
        });
      });
      $$('[data-drawer-close]', drawer).forEach(el => el.addEventListener('click', close));
      const scrim = $('.drawer__scrim', drawer);
      if (scrim) scrim.addEventListener('click', close);
      drawer.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
        if (e.key === 'Tab') trapFocus(e, drawer);
      });
    }

    // Desktop dropdown
    $$('.nav__group').forEach(group => {
      const btn = $('.nav__toggle', group);
      if (!btn) return;
      const setOpen = (state) => {
        group.classList.toggle('is-open', state);
        btn.setAttribute('aria-expanded', String(state));
      };
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        setOpen(!group.classList.contains('is-open'));
      });
      group.addEventListener('keydown', (e) => { if (e.key === 'Escape') { setOpen(false); btn.focus(); } });
      group.addEventListener('focusout', () => {
        setTimeout(() => { if (!group.contains(document.activeElement)) setOpen(false); }, 0);
      });
      document.addEventListener('click', (e) => { if (!group.contains(e.target)) setOpen(false); });
    });

    // Sticky mobile book bar — appears once the hero has scrolled away
    const bar = $('.book-bar');
    if (bar) {
      document.body.classList.add('has-book-bar');
      const trigger = $('[data-bookbar-trigger]') || $('.hero, .page-hero');
      if ('IntersectionObserver' in window && trigger) {
        new IntersectionObserver(([entry]) => {
          bar.classList.toggle('is-visible', !entry.isIntersecting);
        }, { rootMargin: '-120px 0px 0px 0px' }).observe(trigger);
      } else {
        bar.classList.add('is-visible');
      }
    }
  }

  function trapFocus(e, container) {
    const focusables = $$('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', container)
      .filter(el => el.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0], last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ------------------------------------------------------------------
     Scroll reveal
     ------------------------------------------------------------------ */
  function initReveal() {
    const items = $$('.reveal');
    if (!items.length) return;
    if (!('IntersectionObserver' in window) ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach(el => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach(el => io.observe(el));
  }

  /* ------------------------------------------------------------------
     Accordions
     ------------------------------------------------------------------ */
  function initAccordions() {
    $$('.accordion__trigger').forEach(btn => {
      btn.addEventListener('click', () => {
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        if (panel) panel.classList.toggle('is-open', !open);
      });
    });
  }

  /* ------------------------------------------------------------------
     Cart  [SHOPIFY] — replaced by /cart.js AJAX API on the real store
     ------------------------------------------------------------------ */
  const Cart = {
    items: [],
    load() {
      try { this.items = JSON.parse(Store.get('hphs_cart') || '[]'); }
      catch (e) { this.items = []; }
      if (!Array.isArray(this.items)) this.items = [];
    },
    save() { Store.set('hphs_cart', JSON.stringify(this.items)); this.render(); },
    add(handle, variant, qty) {
      const p = (D.PRODUCTS || []).find(x => x.handle === handle);
      if (!p) return;
      const v = variant || (p.variants ? p.variants.options[0] : '');
      const found = this.items.find(i => i.handle === handle && i.variant === v);
      if (found) found.qty += (qty || 1);
      else this.items.push({ handle: handle, title: p.title, price: p.price, variant: v, qty: qty || 1 });
      this.save();
      toast(p.title + ' added to your bag');
      this.open();
    },
    setQty(index, qty) {
      if (!this.items[index]) return;
      if (qty < 1) return this.remove(index);
      this.items[index].qty = qty;
      this.save();
    },
    remove(index) {
      const removed = this.items.splice(index, 1)[0];
      this.save();
      if (removed) toast(removed.title + ' removed');
    },
    count() { return this.items.reduce((n, i) => n + i.qty, 0); },
    subtotal() { return this.items.reduce((n, i) => n + i.price * i.qty, 0); },
    open() {
      const el = $('#cart-drawer');
      if (!el) return;
      el.classList.add('is-open');
      document.body.classList.add('is-locked');
      const close = $('[data-cart-close]', el);
      if (close) close.focus();
    },
    close() {
      const el = $('#cart-drawer');
      if (!el) return;
      el.classList.remove('is-open');
      document.body.classList.remove('is-locked');
    },
    render() {
      const badge = $('#cart-count');
      if (badge) {
        badge.textContent = this.count();
        badge.classList.toggle('is-visible', this.count() > 0);
      }
      const list = $('#cart-items');
      if (!list) return;
      const foot = $('#cart-foot');

      if (!this.items.length) {
        list.innerHTML = '<li class="cart__empty"><p>Your bag is empty.</p>' +
          '<p><a class="link-arrow" href="shop.html">Browse the shop</a></p></li>';
        if (foot) foot.hidden = true;
        return;
      }
      if (foot) foot.hidden = false;

      list.innerHTML = this.items.map((item, i) => `
        <li>
          <div class="cart-item">
            <figure class="ph ph--product"><span class="ph__label"></span></figure>
            <div>
              <p class="cart-item__name">${item.title}</p>
              <p class="cart-item__variant">${item.variant || ''} · ${money(item.price)}</p>
              <div class="cart-item__row">
                <span class="qty">
                  <button type="button" data-qty="-1" data-index="${i}" aria-label="Reduce quantity of ${item.title}">&minus;</button>
                  <output aria-label="Quantity">${item.qty}</output>
                  <button type="button" data-qty="1" data-index="${i}" aria-label="Increase quantity of ${item.title}">+</button>
                </span>
                <button type="button" class="cart-item__remove" data-remove="${i}">Remove</button>
              </div>
            </div>
          </div>
        </li>`).join('');

      const sub = $('#cart-subtotal');
      if (sub) sub.textContent = money(this.subtotal());
    }
  };

  function initCart() {
    Cart.load();
    Cart.render();

    $$('[data-cart-open]').forEach(b => b.addEventListener('click', (e) => { e.preventDefault(); Cart.open(); }));
    $$('[data-cart-close]').forEach(b => b.addEventListener('click', () => Cart.close()));
    const scrim = $('.cart__scrim');
    if (scrim) scrim.addEventListener('click', () => Cart.close());

    const drawer = $('#cart-drawer');
    if (drawer) {
      drawer.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') Cart.close();
        if (e.key === 'Tab') trapFocus(e, drawer);
      });
      drawer.addEventListener('click', (e) => {
        const q = e.target.closest('[data-qty]');
        if (q) {
          const i = Number(q.dataset.index);
          Cart.setQty(i, Cart.items[i].qty + Number(q.dataset.qty));
        }
        const r = e.target.closest('[data-remove]');
        if (r) Cart.remove(Number(r.dataset.remove));
      });
    }

    document.addEventListener('click', (e) => {
      const add = e.target.closest('[data-add-to-cart]');
      if (!add) return;
      e.preventDefault();
      const qtyInput = add.dataset.qtyFrom ? $('#' + add.dataset.qtyFrom) : null;
      Cart.add(add.dataset.addToCart, add.dataset.variant || '', qtyInput ? Number(qtyInput.value) : 1);
    });

    const checkout = $('#cart-checkout');
    if (checkout) checkout.addEventListener('click', (e) => {
      e.preventDefault();
      // [SHOPIFY] real implementation posts to /cart and redirects to checkout
      toast('Prototype only — checkout is not connected');
    });
  }

  /* ------------------------------------------------------------------
     Forms  [HUBSPOT] — real build posts to a HubSpot form endpoint
     ------------------------------------------------------------------ */
  function initForms() {
    $$('form[data-mock-form]').forEach(form => {
      const status = $('.form-status', form);

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        let firstBad = null;

        $$('[required]', form).forEach(field => {
          const wrap = field.closest('.field') || form;
          const err = $('.error-text', wrap);
          let msg = '';

          if (!field.value.trim()) {
            msg = 'This field is required.';
          } else if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(field.value.trim())) {
            msg = 'Enter an email address we can reply to.';
          } else if (field.type === 'tel' && field.value.trim().replace(/\D/g, '').length < 7) {
            msg = 'Enter a phone number with at least 7 digits.';
          }

          field.setAttribute('aria-invalid', msg ? 'true' : 'false');
          if (err) err.textContent = msg;
          if (msg && !firstBad) firstBad = field;
        });

        if (firstBad) { firstBad.focus(); return; }

        if (status) {
          status.classList.add('is-visible');
          status.setAttribute('tabindex', '-1');
          status.focus();
          status.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } else {
          toast(form.dataset.mockForm || 'Sent');
        }
        form.reset();
      });

      $$('input, textarea, select', form).forEach(field => {
        field.addEventListener('input', () => {
          if (field.getAttribute('aria-invalid') !== 'true') return;
          field.setAttribute('aria-invalid', 'false');
          const wrap = field.closest('.field');
          const err = wrap && $('.error-text', wrap);
          if (err) err.textContent = '';
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     Filter grids — shop, stories, services
     ------------------------------------------------------------------ */
  function initFilters() {
    $$('[data-filter-root]').forEach(root => {
      const chips = $$('[data-filter]', root);
      const search = $('[data-filter-search]', root);
      const cards = $$('[data-cat]', root);
      const count = $('[data-filter-count]', root);
      const empty = $('[data-filter-empty]', root);
      let active = 'all';

      function apply() {
        const q = (search ? search.value : '').trim().toLowerCase();
        let shown = 0;
        cards.forEach(card => {
          const matchCat = active === 'all' || card.dataset.cat === active;
          const matchText = !q || (card.dataset.search || card.textContent).toLowerCase().includes(q);
          const show = matchCat && matchText;
          card.hidden = !show;
          if (show) shown++;
        });
        if (count) count.textContent = shown + (shown === 1 ? ' item' : ' items');
        if (empty) empty.hidden = shown > 0;
      }

      chips.forEach(chip => {
        chip.addEventListener('click', () => {
          chips.forEach(c => c.setAttribute('aria-pressed', 'false'));
          chip.setAttribute('aria-pressed', 'true');
          active = chip.dataset.filter;
          apply();
        });
      });
      if (search) search.addEventListener('input', apply);
      apply();
    });
  }

  /* ------------------------------------------------------------------
     Booking mock  [BOOKSY]
     The real page embeds the Booksy widget in #booksy-mount. Everything
     below is a visual stand-in so the surrounding journey can be reviewed.
     ------------------------------------------------------------------ */
  function initBooking() {
  const root = $('[data-booking]');
  if (!root) return;

  // 1. Add `date` property to the state object
  const state = { service: null, barber: null, date: null, time: null };

  function syncSummary() {
    const set = (id, val, fallback) => {
      const el = $('#' + id);
      if (el) el.textContent = val || fallback;
    };
    set('sum-service', state.service, 'Not chosen');
    set('sum-barber', state.barber, 'Any available');
    set('sum-date', state.date, 'Not chosen'); // 2. Update Date summary panel
    set('sum-time', state.time, 'Not chosen');

    const go = $('#booking-continue');
    // 3. Update validation check to require service, date, and time
    if (go) go.setAttribute('aria-disabled', String(!(state.service && state.date && state.time)));
  }

  // 4. Listen for date selection changes
  const dateInput = $('#booking-date', root);
  if (dateInput) {
    const today = new Date().toISOString().split('T')[0];
    dateInput.setAttribute('min', today);

    dateInput.addEventListener('change', (e) => {
      const val = e.target.value;
      if (val) {
        const [year, month, day] = val.split('-');
        state.date = `${day}/${month}/${year}`;
      } else {
        state.date = null;
      }
      syncSummary();
    });
  }

  root.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-choose]');
    // Skip input elements to avoid conflicts with datepicker clicking
    if (!btn || btn.disabled || btn.tagName === 'INPUT') return;
    const key = btn.dataset.choose;
    const group = btn.closest('[data-choice-group]');
    if (group) $$('[data-choose="' + key + '"]', group).forEach(b => b.setAttribute('aria-pressed', 'false'));
    btn.setAttribute('aria-pressed', 'true');
    state[key] = btn.dataset.value || btn.textContent.trim();
    syncSummary();
  });

  // Preselect from URL parameters if provided
  const params = new URLSearchParams(window.location.search);
  ['service', 'barber'].forEach(key => {
    const val = params.get(key);
    if (!val) return;
    const btn = $('[data-choose="' + key + '"][data-value="' + CSS.escape(val) + '"]', root);
    if (btn) btn.click();
  });

  const go = $('#booking-continue');
  if (go) go.addEventListener('click', (e) => {
    e.preventDefault();
    if (go.getAttribute('aria-disabled') === 'true') {
      toast('Choose a service, date, and time first');
      return;
    }
    //toast('Prototype — this hands off to Booksy to confirm the appointment');
  });

  syncSummary();
}

  /* ------------------------------------------------------------------
     Detail pages driven by ?handle= (barber, product, story)
     Shopify note: these become real templates — product.liquid,
     article.liquid, and a metaobject template for barbers.
     ------------------------------------------------------------------ */
  function param(name) { return new URLSearchParams(window.location.search).get(name); }

  function initBarberDetail() {
    const root = $('[data-barber-detail]');
    if (!root) return;
    const list = D.BARBERS || [];
    const b = list.find(x => x.handle === param('barber')) || list[0];
    if (!b) return;

    document.title = b.name + ' — Barber at Hyde Park Hair Salon & Barber, Chicago';
    const fill = (sel, val) => $$(sel, root).forEach(el => { el.innerHTML = val; });
    fill('[data-field="name"]', b.name);
    fill('[data-field="short"]', b.short);
    fill('[data-field="role"]', b.role);
    fill('[data-field="bio"]', b.bio);
    fill('[data-field="philosophy"]', b.philosophy);

    // Real portrait replaces the placeholder frame.
    $$('[data-field="portrait"]', root).forEach(fig => {
      fig.classList.add('ph--photo', 'ph--top');
      fig.innerHTML = '<img src="' + b.img + '" alt="' + b.alt + '" decoding="async">';
    });

    const tags = $('[data-field="specialties"]', root);
    if (tags) tags.innerHTML = b.specialties.map(s => `<li>${s}</li>`).join('');

    const meta = $('[data-field="meta"]', root);
    if (meta) meta.innerHTML = (b.meta || []).map(m => `<div><dt>${m[0]}</dt><dd>${m[1]}</dd></div>`).join('');

    const svc = $('[data-field="services"]', root);
    if (svc) {
      svc.innerHTML = (D.SERVICES || []).map(s => `
        <div>
          <dt>${s.title}</dt>
          <dd>${s.price} · ${s.duration}</dd>
        </div>`).join('');
    }

    $$('[data-field="booklink"]', root).forEach(a => {
      a.href = b.book;
      if (b.booktype === 'booksy') {
        a.textContent = 'Book with ' + b.short + ' on Booksy';
        a.target = '_blank';
        a.rel = 'noopener';
      } else {
        a.textContent = 'Call to book with ' + b.short;
        a.removeAttribute('target');
      }
    });
  }

  function initProductDetail() {
    const root = $('[data-product-detail]');
    if (!root) return;
    const list = D.PRODUCTS || [];
    const p = list.find(x => x.handle === param('product')) || list[0];
    if (!p) return;

    document.title = p.title + ' — HPHS Shop';
    const fill = (sel, val) => $$(sel, root).forEach(el => { el.textContent = val; });
    fill('[data-field="title"]', p.title);
    fill('[data-field="category"]', p.category);
    fill('[data-field="price"]', money(p.price));
    fill('[data-field="excerpt"]', p.excerpt);
    fill('[data-field="story"]', p.story);
    fill('[data-field="photo"]', p.photo);
    fill('[data-field="variantLabel"]', p.variants ? p.variants.label : '');

    const swatches = $('[data-field="variants"]', root);
    let variant = p.variants ? p.variants.options[0] : '';
    if (swatches && p.variants) {
      swatches.innerHTML = p.variants.options.map((o, i) =>
        `<button type="button" class="swatch" aria-pressed="${i === 0}">${o}</button>`).join('');
      swatches.addEventListener('click', (e) => {
        const b = e.target.closest('.swatch');
        if (!b) return;
        $$('.swatch', swatches).forEach(s => s.setAttribute('aria-pressed', 'false'));
        b.setAttribute('aria-pressed', 'true');
        variant = b.textContent;
        const addBtn = $('[data-add-to-cart]', root);
        if (addBtn) addBtn.dataset.variant = variant;
      });
    }

    const addBtn = $('[data-add-to-cart]', root);
    if (addBtn) { addBtn.dataset.addToCart = p.handle; addBtn.dataset.variant = variant; }

    // Gallery thumbs
    const thumbs = $$('[data-thumb]', root);
    const main = $('[data-main-image]', root);
    thumbs.forEach(t => t.addEventListener('click', () => {
      thumbs.forEach(x => x.setAttribute('aria-pressed', 'false'));
      t.setAttribute('aria-pressed', 'true');
      if (main) $('.ph__label', main).textContent = t.dataset.thumb;
    }));

    // Related
    const rel = $('[data-field="related"]', root);
    if (rel) {
      rel.innerHTML = list.filter(x => x.handle !== p.handle).slice(0, 4).map(x => `
        <article class="product-card">
          <span class="badge-concept">Concept</span>
          <a class="product-card__link" href="product.html?product=${x.handle}">
            <figure class="ph ph--product ph--square"><span class="ph__label">${x.photo}</span></figure>
            <div class="product-card__body">
              <h3 class="product-card__name">${x.title}</h3>
              <p class="product-card__price">${money(x.price)}</p>
            </div>
          </a>
        </article>`).join('');
    }
  }

  function initStoryDetail() {
    const root = $('[data-story-detail]');
    if (!root) return;
    const list = D.STORIES || [];
    const s = list.find(x => x.handle === param('story')) || list[0];
    if (!s) return;

    document.title = s.title + ' — Stories — HPHS';
    const fill = (sel, val) => $$(sel, root).forEach(el => { el.textContent = val; });
    fill('[data-field="title"]', s.title);
    fill('[data-field="category"]', s.category);
    fill('[data-field="date"]', s.date);
    fill('[data-field="read"]', s.read);
    fill('[data-field="excerpt"]', s.excerpt);
    fill('[data-field="photo"]', s.photo);

    const rel = $('[data-field="related"]', root);
    if (rel) {
      rel.innerHTML = list.filter(x => x.handle !== s.handle).slice(0, 3).map(x => `
        <a class="story-card" href="story.html?story=${x.handle}">
          <figure class="ph ph--archival ph--wide"><span class="ph__label">${x.photo}</span></figure>
          <p class="story-card__cat">${x.category}</p>
          <h3 class="story-card__title">${x.title}</h3>
          <p class="story-card__meta">${x.read} read</p>
        </a>`).join('');
    }
  }

  /* ------------------------------------------------------------------
     Long-form chapter nav (Legacy page)
     ------------------------------------------------------------------ */
  function initChapterNav() {
    const nav = $('.chapter-nav');
    if (!nav || !('IntersectionObserver' in window)) return;
    const links = $$('a', nav);
    const targets = links.map(a => document.querySelector(a.getAttribute('href'))).filter(Boolean);
    if (!targets.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        links.forEach(a => a.classList.toggle('is-active', a.getAttribute('href') === '#' + entry.target.id));
      });
    }, { rootMargin: '-25% 0px -65% 0px' });
    targets.forEach(t => io.observe(t));
  }

  /* ------------------------------------------------------------------
     Mark current page in nav
     ------------------------------------------------------------------ */
  function initCurrentPage() {
    const here = (window.location.pathname.split('/').pop() || 'index.html');
    $$('.nav__link, .drawer__nav a').forEach(a => {
      const href = (a.getAttribute('href') || '').split('?')[0];
      if (href === here) a.setAttribute('aria-current', 'page');
    });
  }


  /* ------------------------------------------------------------------
     Image resilience.
     Photography is currently referenced from the client's live domain.
     If any host blocks hotlinking, goes down, or a file is renamed, the
     frame falls back to the labelled placeholder treatment rather than
     leaving an empty box. The page then still reads as intentional.
     'error' does not bubble, so this listens in the capture phase.
     ------------------------------------------------------------------ */
  function initImageFallback() {
    document.addEventListener('error', function (e) {
      const img = e.target;
      if (!img || img.tagName !== 'IMG') return;
      const frame = img.closest('.ph, .xp-panel__media, .xp-bg');
      if (!frame) return;

      frame.classList.add('is-broken');
      const alt = img.getAttribute('alt') || '';
      img.remove();

      if (frame.classList.contains('ph') && alt && !$('.ph__label', frame)) {
        const label = document.createElement('span');
        label.className = 'ph__label';
        label.textContent = alt;
        frame.appendChild(label);
      }
    }, true);
  }

  /* ------------------------------------------------------------------
     Landing Experience sliding action & panel dynamics
     ------------------------------------------------------------------ */
  function initExperience() {
    const page    = $('.xp-page');
    const panels  = $('.xp-panels');
    const items   = $$('.xp-panel');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

    if (!page || !panels || !items.length) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => document.body.classList.add('xp-ready'));
    });

    function setActive(el) {
      items.forEach(p => p.classList.toggle('is-active', p === el));
      panels.classList.toggle('is-engaged', Boolean(el));
    }

    if (canHover.matches) {
      items.forEach(panel => {
        panel.addEventListener('mouseenter', () => setActive(panel));
        panel.addEventListener('focus', () => setActive(panel));
        panel.addEventListener('blur', () => setActive(null));
      });
      panels.addEventListener('mouseleave', () => setActive(null));
    }

    if (canHover.matches && !reduced.matches) {
      const MAX = 6;
      let frame = null;
      let last = null;

      panels.addEventListener('mousemove', (e) => {
        last = e;
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          const panel = last.target.closest('.xp-panel');
          if (!panel) return;
          const media = $('.xp-panel__media', panel);
          if (!media) return;
          const r = panel.getBoundingClientRect();
          const dx = ((last.clientX - r.left) / r.width - 0.5) * 2;
          const dy = ((last.clientY - r.top) / r.height - 0.5) * 2;
          media.style.transform = 'translate3d(' + (-dx * MAX).toFixed(2) + 'px,' +
                                                   (-dy * MAX).toFixed(2) + 'px,0)';
        });
      });

      panels.addEventListener('mouseleave', () => {
        items.forEach(p => {
          const m = $('.xp-panel__media', p);
          if (m) m.style.transform = '';
        });
      });
    }

    const dots    = $$('.xp-dots__dot');
    const current = $('#xp-current');

    if (dots.length) {
      let ticking = false;
      const sync = () => {
        const isMobile = window.innerWidth <= 699;
        let index = 0;
        let best = Infinity;

        if (isMobile) {
          const viewportCenter = window.innerHeight / 2;
          items.forEach((p, i) => {
            const rect = p.getBoundingClientRect();
            const cardCenter = rect.top + rect.height / 2;
            const gap = Math.abs(cardCenter - viewportCenter);
            if (gap < best) { best = gap; index = i; }
          });
        } else {
          const mid = panels.scrollLeft + panels.clientWidth / 2;
          items.forEach((p, i) => {
            const centre = p.offsetLeft + p.offsetWidth / 2;
            const gap = Math.abs(centre - mid);
            if (gap < best) { best = gap; index = i; }
          });
        }
        dots.forEach((d, i) => d.classList.toggle('is-on', i === index));
        if (current) current.textContent = String(index + 1).padStart(2, '0');
      };

      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { sync(); ticking = false; });
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      panels.addEventListener('scroll', onScroll, { passive: true });
      sync();
    }

    items.forEach(panel => {
      panel.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        const href = panel.getAttribute('href');
        if (!href || href.charAt(0) === '#') return;
        if (panel.target === '_blank') return;
        if (reduced.matches) return;

        e.preventDefault();
        setActive(panel);
        panel.classList.add('is-leaving');
        page.classList.add('is-leaving');
        setTimeout(() => { window.location.href = href; }, 340);
      });
    });

    const xpHead = $('.xp-head');
    if (xpHead) {
      const syncXpHead = () => {
        xpHead.classList.toggle('is-solid', window.scrollY > 24);
      };
      window.addEventListener('scroll', syncXpHead, { passive: true });
      syncXpHead();
    }
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */
  document.addEventListener('DOMContentLoaded', function () {
    initHeader();
    initExperience();
    initImageFallback();
    initCurrentPage();
    initReveal();
    initAccordions();
    initCart();
    initForms();
    initFilters();
    initBooking();
    initBarberDetail();
    //initProductDetail();
    initStoryDetail();
    initChapterNav();
  });

  window.HPHS_UI = { toast: toast, cart: Cart };
})();
